import messengerWebSocketService from '@widgets/messenger/services/messengerWebSocketService'
import type { MessengerWebSocketEvent } from '@widgets/messenger/types/api/websocket'
import { throttle } from 'lodash-es'
import { messengerMessageMaxLength } from '@widgets/messenger/const'
import type { RefOrGetter } from '@/types'
import { computed, watchEffect } from '@/composition/vue/compositionApi'
import { getValueFromRefOrGetter } from '@/composition/getValueFromRefOrGetter'

export const useWidgetChatTyping = (message: RefOrGetter<string>, isMessageSending: RefOrGetter<boolean>) => {
  const messageRef = computed(() => getValueFromRefOrGetter(message))
  const isMessageSendingRef = computed(() => getValueFromRefOrGetter(isMessageSending))

  const sendStartTyping = async (messageValue: string) => {
    const channel = messengerWebSocketService.getSubscriptionToken()?.channel
    if (!channel) {
      return
    }
    const truncatedMessageValue =
      messageValue.length > messengerMessageMaxLength ? messageValue.slice(0, messengerMessageMaxLength) : messageValue

    await messengerWebSocketService.publish(channel, {
      type: 'startTyping',
      payload: {
        type: 'contactTyping',
        text: truncatedMessageValue,
      },
      timestamp: new Date().toISOString(),
    } satisfies MessengerWebSocketEvent<'startTyping'>)
  }

  const sendEndTyping = async () => {
    const channel = messengerWebSocketService.getSubscriptionToken()?.channel
    if (!channel) {
      return
    }
    await messengerWebSocketService.publish(channel, {
      type: 'endTyping',
      payload: {
        type: 'contactTyping',
        text: '',
      },
      timestamp: new Date().toISOString(),
    } satisfies MessengerWebSocketEvent<'endTyping'>)
  }

  const throttledSendStartTyping = throttle(sendStartTyping, 1000)

  watchEffect(() => {
    if (messageRef.value) {
      throttledSendStartTyping(messageRef.value)
    } else if (!isMessageSendingRef.value) {
      throttledSendStartTyping.cancel()
      sendEndTyping()
    }
  })
}
